export enum PathOption {
  Playground = "playground",
  Datamates = "datamates",
  Dbt = "dbt",
}

export interface PathConfig {
  option: PathOption;
  label: string;
  textColor: string;
  borderColor: string;
  bgColor: string;
  hoverBgColor: string;
  cardBgColor: string;
  description: string;
}

export const pathConfigs: Record<PathOption, PathConfig> = {
  [PathOption.Playground]: {
    option: PathOption.Playground,
    label: "Studio",
    textColor: "al-text-green-500",
    borderColor: "al-border-green-500",
    bgColor: "al-bg-green-500",
    hoverBgColor: "hover:al-bg-green-500",
    cardBgColor: "al-bg-green-50",
    description: "Studio is a specialized agent for data engineering tasks",
  },
  [PathOption.Datamates]: {
    option: PathOption.Datamates,
    label: "Datamates",
    textColor: "al-text-blue-500",
    borderColor: "al-border-blue-500",
    bgColor: "al-bg-blue-500",
    hoverBgColor: "hover:al-bg-blue-500",
    cardBgColor: "al-bg-blue-50",
    description:
      "DataMates is an end-to-end platform for building AI TeamMates with data tools, memory, knowledge and guardrails.",
  },
  [PathOption.Dbt]: {
    option: PathOption.Dbt,
    label: "Power User for dbt",
    textColor: "al-text-orange-500",
    borderColor: "al-border-orange-500",
    bgColor: "al-bg-orange-500",
    hoverBgColor: "hover:al-bg-orange-500",
    cardBgColor: "al-bg-orange-50",
    description:
      "Power User for dbt is a VSCode extension that supercharges your dbt development workflow.",
  },
};

export interface DropdownItem {
  title: string;
  subtitle: string;
  tutorial: string;
}

export const dropdownMenuData: Record<PathOption, DropdownItem[]> = {
  [PathOption.Playground]: [
    {
      title: "Setup your dbt project artifacts",
      subtitle:
        "Upload your dbt project artifacts including manifest.json and catalog.json to get started with Studio.",
      tutorial: `<iframe src="https://app.supademo.com/embed/cmjsuh5iv8h2b3zz2ao9u9xgk?embed_v=2" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh"></iframe>`,
    },
    {
      title: "View Prompt Library and Examples",
      subtitle: "Explore pre-built prompts and examples to accelerate your workflow",
      tutorial: `<iframe src="https://app.supademo.com/embed/cmjsvgkca8hsa3zz22b0fg7i7?embed_v=2" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style=" width: 100%; height: 84vh;"></iframe>`,
    },
  ],
  [PathOption.Datamates]: [
    {
      title: "Create your first Datamate",
      subtitle:
        "You can choose from pre-built datamate templates, or create your own by fine tuning integration capabilities and guardrails. You can also contact us over Slack or Chat or book a meeting to help set it up for you.",
      tutorial: `<iframe src="https://app.supademo.com/embed/cmamfizd70jav2gbpbcvvzf6c?embed_v=2" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh"></iframe>`,
    },
    {
      title: "Install Datamates Extension in Github Copilot, Cursor, Windsurf or Cline",
      subtitle: "Connect your Datamate to your favorite AI coding assistant",
      tutorial: `<iframe src="https://app.supademo.com/embed/cmbq3cay8b6u6sn1rplmsnavj?embed_v=2" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style=" width: 100%; height: 84vh;"></iframe>`,
    },
    {
      title: "Explore Examples",
      subtitle: "Update dbt project from ticket requirements",
      tutorial: `<iframe src="https://app.supademo.com/embed/cmaotsrmk151qho3rxjs665zf?embed_v=2" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style=" width: 100%; height: 84vh;"></iframe>`,
    },
  ],
  [PathOption.Dbt]: [
    {
      title: "Install and setup Power User for dbt with the key",
      subtitle: "Get your API key and configure the extension in VS Code",
      tutorial: `<iframe src="https://app.supademo.com/embed/clnjpwl3u07x4pedv9ifjfuf9" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh;"></iframe>`,
    },
    {
      title: "Column lineage",
      subtitle: "Visualize data flow and dependencies across your models",
      tutorial: `<iframe src="https://app.supademo.com/embed/clncl1wuj44z8pezp7iw9vnj5" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh;"></iframe>`,
    },
    {
      title: "dbt query results preview, analysis and export",
      subtitle: "Instantly test your dbt query, analyze the results or export them",
      tutorial: `<iframe src="https://app.supademo.com/embed/S4uCGtgs_jisbJs5SfJzZ" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh;"></iframe>`,
    },
    {
      title: "Auto-complete and quick navigation",
      subtitle:
        "Smart auto-complete for models, macros, columns in VS Code. Execute child / parent models and tests with a click",
      tutorial: `<iframe src="https://app.supademo.com/embed/clncaqx253xyypezplang48gr" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh;"></iframe>`,
    },
    {
      title: "dbt documentation generation and easy edition",
      subtitle: "Generate and edit documentation directly in VS Code with AI assistance",
      tutorial: `<iframe src="https://app.supademo.com/embed/clncbea4j3yl1pezp0zp0pfys" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh;"></iframe>`,
    },
    {
      title: "Project health check",
      subtitle:
        "Identify issues with your dbt project like missing models / columns in database, missing documentation etc.",
      tutorial: `<iframe src="https://app.supademo.com/embed/clof2pnhl262fpegd7straszw" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh;"></iframe>`,
    },
    {
      title: "Query explanation",
      subtitle: "Get summary and explanation of complex dbt queries",
      tutorial: `<iframe src="https://app.supademo.com/embed/clog0tipl3n35pegdy28pxe06" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 100%; height: 84vh;"></iframe>`,
    },
  ],
};
